"use client";

import { useState } from "react";
import { Check, Shield, Zap } from "lucide-react";

import { PricingCard } from "@/components/ui/pricing-card";
import { cn, formatUsdCurrency } from "@/lib/utils";

type BillingCycle = "monthly" | "annual";

const ANNUAL_DISCOUNT_PERCENT = 20;

const MAIN_TIERS = [
  {
    name: "Personal",
    description: "Your everyday AI companion",
    bestFor: "Best for individuals",
    monthlyPrice: 12.99,
    annualTotal: 124.7,
    features: [
      "Included AI usage",
      "All messaging channels",
      "Voice notes",
      "Reminders & scheduling",
      "Standard support",
    ],
    cta: "Start free trial",
    popular: false,
  },
  {
    name: "Plus",
    description: "More power, more context",
    bestFor: "Best for busy professionals",
    monthlyPrice: 19.99,
    annualTotal: 191.9,
    features: [
      "Included AI usage",
      "All messaging channels",
      "Priority processing",
      "Advanced reminders",
      "Usage analytics",
    ],
    cta: "Start free trial",
    popular: true,
  },
  {
    name: "Pro",
    description: "Maximum capability, no limits",
    bestFor: "Best for power users",
    monthlyPrice: 29.99,
    annualTotal: 287.9,
    features: [
      "Included AI usage",
      "All messaging channels",
      "Priority processing",
      "Advanced analytics",
      "Dedicated support",
    ],
    cta: "Start free trial",
    popular: false,
  },
];

export function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");

  return (
    <section id="pricing" data-testid="section-pricing" className="py-16 sm:py-24">
      <div className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">Pricing</p>
        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
          Simple, honest pricing.
        </h2>
        <p className="mt-3 text-base font-medium leading-relaxed text-foreground/70">
          Start with a 7-day free trial. Add your payment method at checkout. Billing starts
          automatically after 7 days unless you cancel.
        </p>
      </div>

      <div className="mt-8 flex flex-col items-center justify-center gap-3">
        <div
          className="segmented-shell inline-flex gap-1 rounded-full p-1 text-sm font-medium"
          role="group"
          aria-label="Billing cycle"
        >
          {(["monthly", "annual"] as BillingCycle[]).map((cycle) => (
            <button
              key={cycle}
              type="button"
              onClick={() => setBillingCycle(cycle)}
              aria-pressed={billingCycle === cycle}
              className={cn(
                "segmented-item inline-flex items-center gap-2 rounded-full px-5 py-2 transition focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none",
                billingCycle === cycle
                  ? "segmented-item-active text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {cycle === "monthly" ? "Monthly" : "Annual"}
              {cycle === "annual" ? (
                <span className="stamp rounded-full px-2 py-0.5 text-[11px] font-semibold text-foreground/70">
                  Save {ANNUAL_DISCOUNT_PERCENT}%
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-12 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {MAIN_TIERS.map((tier) => {
          const annualPrice = Number((tier.annualTotal / 12).toFixed(2));
          const price = billingCycle === "annual" ? annualPrice : tier.monthlyPrice;
          const billingNote =
            billingCycle === "annual"
              ? `Billed annually (${formatUsdCurrency(tier.annualTotal)}/yr) · Save ${ANNUAL_DISCOUNT_PERCENT}%`
              : "Billed monthly";

          return (
            <PricingCard
              key={tier.name}
              name={tier.name}
              description={tier.description}
              bestFor={tier.bestFor}
              price={price}
              priceUnit="mo"
              billingNote={billingNote}
              features={tier.features}
              popular={tier.popular}
              cta={tier.cta}
              ctaHref={`https://app.dobbix.io/pricing?cycle=${billingCycle}`}
            />
          );
        })}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Shield className="h-4 w-4 text-primary" />
          Cancel anytime
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Zap className="h-4 w-4 text-primary" />
          7-day free trial
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Check className="h-4 w-4 text-primary" />
          Billing starts after trial unless canceled
        </span>
      </div>
    </section>
  );
}
